import { ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label as LabelUI } from '@/components/ui/label';

import { cn } from '@/lib/utils';

import { useTheme } from '@/providers';

import { JoditEditorWrapper } from './JoditEditorWrapper';

type JoditConfig = Record<string, unknown>;

type TextEditorProps = {
  id?: string;
  value?: string;
  label?: ReactNode;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  className?: string;
};

const TextEditor = (props: TextEditorProps) => {
  const {
    id,
    value,
    label,
    disabled,
    'aria-invalid': ariaInvalid,
    onChange,
    onBlur,
    className,
  } = props;

  const { i18n, t } = useTranslation();
  const { theme } = useTheme();
  const generatedId = useId();
  const editorId = id ?? generatedId;

  const [isFocused, setIsFocused] = useState(false);

  // jodit-react re-runs its value-sync useEffect whenever its `value` prop
  // changes and rewrites the editor's innerHTML if `jodit.value !== value`
  // (see node_modules/jodit-react/src/JoditEditor.tsx). Because Jodit
  // normalises HTML internally, even the string Jodit itself emits can fail
  // that equality check on write-back, which resets the caret to position 0.
  //
  // To avoid this, we keep the `value` prop we pass to Jodit referentially
  // stable across typing renders: we only update it when a value arrives
  // that was NOT just emitted by Jodit (i.e. a genuine external change like
  // form reset, setValue, or edit-mode load). `lastEmittedValue` tracks what
  // Jodit last emitted so we can ignore the react-hook-form echo.
  const [editorValue, setEditorValue] = useState<string>(value || '');
  const [lastEmittedValue, setLastEmittedValue] = useState<string>(value || '');

  // React's recommended pattern for syncing derived state from props without
  // useEffect — condition-guarded setState during render.
  const incomingValue = value || '';
  if (incomingValue !== editorValue && incomingValue !== lastEmittedValue) {
    setEditorValue(incomingValue);
    setLastEmittedValue(incomingValue);
  }

  const getJoditLanguage = (lang: string): string => {
    const languageMap: Record<string, string> = {
      en: 'en',
      uk: 'uk',
    };
    return languageMap[lang] || 'en';
  };

  const getJoditTranslations = useMemo(() => {
    const translations: Record<string, string> = {};

    const joditKeys = [
      'Bold',
      'Italic',
      'Underline',
      'Strike through',
      'Clear formatting',
      'Insert link',
      'Unlink',
      'Insert Unordered List',
      'Default',
      'Insert Ordered List',
      'Insert format block',
      'Paragraph',
      'Heading 1',
      'Heading 2',
      'Heading 3',
      'Heading 4',
      'Quote',
      'Code',
      'Undo',
      'Redo',
      'Type something',
      'Insert image',
      'Insert table',
      'Source code',
      'Fullsize',
      'Shrink',
      'Select all',
      'Clear Formatting',
    ];

    joditKeys.forEach((key) => {
      const translationKey = `jodit.${key}`;
      const translated = t(translationKey);

      if (translated && translated !== translationKey) {
        translations[key] = translated;
      }
    });

    return translations;
  }, [t]);

  const config = useMemo((): JoditConfig => {
    const joditLanguage = getJoditLanguage(i18n.language);
    const baseConfig: JoditConfig = {
      readonly: disabled || false,
      placeholder: '',
      language: joditLanguage,
      theme: theme === 'dark' ? 'dark' : 'default',
      toolbarAdaptive: false,
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'eraser',
        '|',
        'ul',
        'ol',
        '|',
        'link',
        '|',
        'paragraph',
        '|',
        'undo',
        'redo',
      ],
      controls: {
        ul: {
          list: ['default'],
        },
        ol: {
          list: ['default'],
        },
      },
      events: {
        focus: () => setIsFocused(true),
        blur: () => setIsFocused(false),
      },
    };

    if (Object.keys(getJoditTranslations).length > 0) {
      baseConfig.i18n = {
        [joditLanguage]: getJoditTranslations,
      };
    }

    return baseConfig;
  }, [i18n.language, getJoditTranslations, disabled, theme]);

  const handleChange = (newContent: string) => {
    // Only update the echo tracker — NOT editorValue — so Jodit's `value`
    // prop stays referentially stable while the user is typing.
    setLastEmittedValue(newContent);
    onChange?.(newContent);
  };

  const handleBlur = (newContent: string) => {
    setLastEmittedValue(newContent);
    onBlur?.(newContent);
  };

  return (
    <div
      className={cn(
        'text-editor-wrapper text-foreground relative',
        isFocused && 'text-editor-focused',
        ariaInvalid && 'text-editor-invalid',
        className,
      )}
    >
      <JoditEditorWrapper
        key={theme}
        id={editorId}
        value={editorValue}
        config={config}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      {label && (
        <>
          <fieldset
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 top-[-5px] z-1 overflow-hidden rounded-md border-2 border-solid bg-transparent px-[9px] transition-colors',
              isFocused && !ariaInvalid && 'border-primary',
              !isFocused && !ariaInvalid && 'border-input',
              ariaInvalid && 'border-destructive',
            )}
          >
            <legend className="invisible block h-[11px] w-auto max-w-full overflow-hidden p-0 text-xs whitespace-nowrap">
              <span className="visible inline-block px-1 opacity-0">
                {label}
              </span>
            </legend>
          </fieldset>
          <LabelUI
            htmlFor={editorId}
            className={cn(
              'bg-card text-muted-foreground pointer-events-none absolute top-0 left-[13px] z-2 -translate-y-1/2 gap-1 rounded-xs px-1 text-xs transition-colors',
              isFocused && !ariaInvalid && 'text-primary',
              ariaInvalid && 'text-destructive',
            )}
          >
            {label}
          </LabelUI>
        </>
      )}
    </div>
  );
};

export { TextEditor };
export type { TextEditorProps };
